import React, { ReactNode, useCallback, useState } from 'react'

import * as SwitchPrimitive from '@radix-ui/react-switch'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'

import Text, { TextSize } from '../../../components/Typography/Text'
import { Size } from '../../types'
import Flex from '../Flex/Flex'
import FormControl from '../FormControl/FormControl'
import { FormControlProps } from '../FormControl/util'

const StyledSwitchRoot = styled(SwitchPrimitive.Root)<{ $size: Size }>`
  position: relative;
  width: ${({ $size }) => ($size === 'sm' ? 32 : 44)}px;
  height: ${({ $size }) => ($size === 'sm' ? 16 : 22)}px;
  background-color: ${({ theme }) => theme.colorTextDisabled};
  border: none;
  border-radius: 11px;
  cursor: pointer;
  &[data-disabled] {
    background-color: ${({ theme }) => theme.colorFill};
    cursor: not-allowed;
  }
  &[data-state='checked']:not([data-disabled]) {
    background-color: ${({ theme }) => theme.colorPrimaryBase};
  }
  &:focus-visible {
    outline: solid 2px ${({ theme }) => theme.colorPrimaryBorder};
    outline-offset: 2px;
  }
`

const StyledThumb = styled(SwitchPrimitive.Thumb)<{ $size: Size }>`
  display: block;
  width: ${({ $size }) => ($size === 'sm' ? 12 : 18)}px;
  height: ${({ $size }) => ($size === 'sm' ? 12 : 18)}px;
  background-color: ${({ theme }) => theme.white};
  border-radius: 50%;
  transition: transform 0.1s linear;
  transform: translateX(2px);
  &[data-state='checked'] {
    transform: translateX(${({ $size }) => ($size === 'sm' ? 18 : 24)}px);
  }
`

type SwitchProps = Omit<FormControlProps, 'ariaLabel'> & {
  align?: 'left' | 'right'
  isDisabled?: boolean
  onValueChange: (value: boolean) => void
  size?: Size
  value: boolean
} & (
    | { ariaLabel: string; children?: never; label?: never }
    // For legacy form purposes, allow both a label and children (whenever we remove the concept of `rightText` we can set label to never here)
    | { ariaLabel?: never; children: React.ReactNode; label?: ReactNode }
    | { ariaLabel?: never; children?: never; label: ReactNode }
  )

const Switch = ({
  align = 'left',
  ariaLabel,
  caption,
  children,
  errorMessage,
  isDisabled,
  isInvalid,
  isRequired,
  label,
  onValueChange,
  size = 'md',
  tooltip,
  value = false,
}: SwitchProps) => {
  const [id] = useState(uuid())
  const handleCheckedChange = useCallback(
    (checked: boolean) => {
      if (isDisabled) {
        return
      }
      onValueChange?.(checked)
    },
    [isDisabled, onValueChange]
  )

  return (
    <FormControl
      caption={caption}
      errorMessage={errorMessage}
      id={id}
      isInvalid={isInvalid}
      isLabelRedundant
      isRequired={isRequired}
      label={label}
      tooltip={tooltip}
    >
      {({ ariaDescribedBy, ariaInvalid, ariaLive }) => (
        <Flex align="center" direction={align === 'left' ? 'row' : 'row-reverse'}>
          <StyledSwitchRoot
            $size={size}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            aria-label={ariaLabel || 'Toggle'}
            aria-live={ariaLive}
            checked={value}
            disabled={isDisabled}
            id={id}
            onCheckedChange={handleCheckedChange}
          >
            <StyledThumb $size={size} />
          </StyledSwitchRoot>
          {children ? (
            <label
              htmlFor={id}
              className={`${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${
                // padding used instead of gap so the gap is clickable
                align === 'left' ? 'pl-2' : 'pr-2'
              }`}
            >
              <Text size={size === 'md' ? TextSize.Base : TextSize.Small}>{children}</Text>
            </label>
          ) : null}
        </Flex>
      )}
    </FormControl>
  )
}

export default React.memo(Switch)
